/**
 * usePublishFanout.ts
 *
 * Hook that encapsulates `PublishFanoutService` orchestration.
 */

import { publishFanoutDepsFromStore } from '@src/services/publish/publishFanoutDepsFromStore';
import { PublishFanoutService } from '@src/services/publish/PublishFanoutService';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useState } from 'react';

import type { PublishFanoutResult } from '@src/services/publish/PublishFanoutService';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { Video } from '@src/types.ts/story';
import type { AspectRatio, VideoResolution } from '@src/types.ts/video';

const logger = LogManager.getLogger('hook:usePublishFanout');

interface UsePublishFanoutInput {
  /**
   * The parent clip to publish, or a function returning it.
   *
   * Pass the function form from any surface that can change the clip between the
   * render and the click. The clip preview replaces a variant rather than editing
   * it, so a clip captured at render time is a different object from the one the
   * store holds a moment later. Awaiting inside the click handler does not help:
   * React's re-render is scheduled, and the panel has closed itself by then.
   */
  mainClip: Video | (() => Video);
  selectedLanguages: Set<string>;
  selectedAspectRatios: Set<AspectRatio>;
  selectedResolutions: Set<VideoResolution>;
  onSuccess?: () => void;
  /**
   * The clip screen's own editor store (`ClipVideoCreatorAdapter`), when this
   * host has one. The clip preview passes it; the Video Clips card kebab has no
   * live editor behind it and passes nothing.
   */
  liveEditorStore?: LiveEditorStore | null;
}

/**
 * The slice of the clip screen's store this hook needs. Optional members
 * because the clip preview hands its adapter over as a `Partial`.
 */
type LiveEditorStore = Partial<
  Pick<
    IVideoCreatorStore,
    'isBackgroundSaving' | 'saveStoryAndVideo' | 'waitForBackgroundSave'
  >
>;

interface UsePublishFanoutReturn {
  /**
   * Dispatch the fan-out. Resolves to the result (records + shared groupId) so
   * callers can transition into a live publishing view, or null on
   * empty-selection / total failure.
   */
  publish: () => Promise<PublishFanoutResult | null>;
  submitting: boolean;
  error: string | null;
}

export function usePublishFanout({
  mainClip,
  selectedLanguages,
  selectedAspectRatios,
  selectedResolutions,
  onSuccess,
  liveEditorStore,
}: UsePublishFanoutInput): UsePublishFanoutReturn {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { renderUsingChunks } = useFlagsCombination(userIdentity.currentRole);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publish = useCallback(async (): Promise<PublishFanoutResult | null> => {
    const variantCount =
      selectedLanguages.size *
      selectedAspectRatios.size *
      selectedResolutions.size;
    if (variantCount === 0) return null;

    setSubmitting(true);
    setError(null);

    try {
      const clip = typeof mainClip === 'function' ? mainClip() : mainClip;
      const deps = publishFanoutDepsFromStore(videoCreator, clip);

      // PLAT-11597 — Content Studio never publishes the ambient store's video,
      // so the default "save the live editor" must not run: that store can hold
      // an unsaved full-length copy, which the save would create and link into
      // the story. Assigned, not spread — `story`/`organization` are getters
      // the service reads lazily.
      deps.saveLiveEditor = async () => {
        // The card kebab: no live editor, and its record already exists.
        if (!liveEditorStore) return;

        // The clip screen auto-saves its own edits. The exception is a variant
        // the AR switch just minted, whose id arrives in a save it dispatched
        // without awaiting — and `expandCells` skips an AR whose video has no
        // id. Asked through the fan-out's own resolver, so the condition is
        // exactly "this publish would find no record".
        const awaitingId = Array.from(selectedAspectRatios).some(
          ar => !deps.getVideoForAspectRatio(ar)?.id,
        );
        if (!awaitingId) return;

        logger.info(
          'Publishing a variant with no id yet; waiting for its save',
          { alreadySaving: liveEditorStore.isBackgroundSaving ?? false },
        );
        try {
          // A save in flight is already the one assigning the id; asking for
          // another only queues a second write behind it. Awaited so the wait
          // below cannot outrun the save's start.
          if (!liveEditorStore.isBackgroundSaving) {
            await liveEditorStore.saveStoryAndVideo?.(
              false,
              true,
              false,
              false,
              true,
            );
          }
          await liveEditorStore.waitForBackgroundSave?.();
        } catch (err) {
          // Never fatal: the stored recipe publishes either way.
          logger.warn('Pre-publish save failed; publishing the stored recipe', {
            error: extractErrorInfo(err),
          });
        }
      };

      const service = new PublishFanoutService(deps);
      const result: PublishFanoutResult = await service.execute({
        languages: Array.from(selectedLanguages),
        aspectRatios: Array.from(selectedAspectRatios),
        resLevels: Array.from(selectedResolutions),
        renderUsingChunks: renderUsingChunks || false,
      });

      if (result.dispatchErrors.size > 0) {
        const failedCount = result.dispatchErrors.size;
        setError(
          `${result.records.length - failedCount} of ${result.records.length} renders dispatched. ${failedCount} failed.`,
        );
        logger.warn('Partial publish failure', {
          total: result.records.length,
          failed: failedCount,
        });
      } else {
        logger.info('Publish fan-out complete', {
          records: result.records.length,
        });
        onSuccess?.();
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      logger.error('Publish fan-out failed', { error: err });
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [
    videoCreator,
    mainClip,
    selectedLanguages,
    selectedAspectRatios,
    selectedResolutions,
    renderUsingChunks,
    onSuccess,
    liveEditorStore,
  ]);

  return { publish, submitting, error };
}
